import { useSelector } from 'react-redux';
import { Bar } from 'react-chartjs-2';
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { formatCurrency } from '@utils/common';
import {
  customXandYAxisLabelPlugin,
  hoverLinePlugin,
  initiateUnderwritingChartsTooltip,
} from './utils';

import { RootState } from '@stores/store';

const ALL_VALUE = '__ALL__';

const CommissionsChart = ({
  xLabel,
  yLabel,
  isReferrers,
  payoutOptions,
  plotData,
  fetchCharts,
  selectedOptions,
  setSelectedOptions,
  handleDownload,
}: {
  xLabel: string;
  yLabel: string;
  isReferrers: boolean;
  payoutOptions: string[];
  fetchCharts: (inputProps?: { referrer?: string; payoutMonth?: string[] }) => Promise<void>;
  plotData: {
    gameId: string;
    dealName: string;
    referrer: string;
    referrerId: string;
    commission: number;
  }[];
  selectedOptions: string[];
  setSelectedOptions: (options: string[]) => void;
  handleDownload: (payoutMonths: string[]) => Promise<void>;
}) => {
  const userRole = useSelector((state: RootState) => state.UserData.user.role);

  const handleChange = (_: string, value: string[]) => {
    if (value.includes(ALL_VALUE)) {
      const isAllSelected = selectedOptions.length === payoutOptions.length;
      setSelectedOptions(isAllSelected ? [] : payoutOptions);
      fetchCharts({
        payoutMonth: isAllSelected ? [] : payoutOptions,
      });
    } else {
      setSelectedOptions(value);
      fetchCharts({
        payoutMonth: value,
      });
    }
  };

  const isAllSelected = selectedOptions.length === payoutOptions.length;
  // Group and sum commissions by referrer
  const aggregatedDataForDeals: Record<string, number> = plotData?.reduce(
    (acc: Record<string, number>, curr) => {
      acc[curr.dealName] = (acc[curr.dealName] || 0) + curr.commission;
      return acc;
    },
    {},
  );
  const aggregatedDataForReferrers = plotData.reduce((acc: Record<string, number>, curr) => {
    acc[curr.referrer] = (acc[curr.referrer] || 0) + curr.commission;
    return acc;
  }, {});

  const aggregatedData = isReferrers ? aggregatedDataForReferrers : aggregatedDataForDeals;

  const labels = Object.keys(aggregatedData);

  const data = {
    labels: labels,
    datasets: [
      {
        label: 'Commissions (USD)',
        data: Object.values(aggregatedData),
        fill: true,
        backgroundColor: '#7FBCAD',
        borderRadius: {
          topLeft: 8,
          topRight: 8,
          bottomLeft: 0,
          bottomRight: 0,
        },
        borderSkipped: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHitRadius: 4,
        pointBorderWidth: 2,
        pointHoverBorderWidth: 2,
        pointBackgroundColor: '#FFF',
        pointHoverBackgroundColor: '#FFF',
      },
    ],
  };

  const customTooltipforReferrers = {
    enabled: false,
    external: function (context: any) {
      const { tooltipEl, tooltipModel } = initiateUnderwritingChartsTooltip(context);

      const referrer = tooltipModel.title?.[0] ?? 'Unknown';

      // Filter and group by dealName for the current referrer
      const referrerDeals = plotData
        .filter((item: any) => item.referrer === referrer)
        .reduce((acc: Record<string, number>, curr: any) => {
          acc[curr.dealName] = (acc[curr.dealName] || 0) + Number(curr.commission);
          return acc;
        }, {});

      // Tooltip header
      let tooltipContent = `<div style="font-weight: bold; margin-bottom: 8px; font-size: 1rem;">${referrer}</div>`;

      // Table headers
      tooltipContent += `
          <div style="display: flex; justify-content: space-between; font-weight: 500; color: #6C6C6C; margin-bottom: 4px; font-size: 0.75rem;">
            <div>Deal</div>
            <div>Commissions</div>
          </div>
        `;

      // Deal rows
      Object.entries(referrerDeals).forEach(([deal, commission]) => {
        tooltipContent += `
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; color: #6C6C6C; font-size: 0.875rem; font-weight: 700;">
              <div>${deal}</div>
              <div>${formatCurrency(commission.toFixed(2)) || '$0'}</div>
            </div>
          `;
      });

      // Tooltip styling
      tooltipEl.style.width = '250px';
      tooltipEl.style.height = 'auto';
      tooltipEl.style.padding = '12px';
      tooltipEl.style.background = '#fff';
      tooltipEl.style.borderRadius = '8px';
      tooltipEl.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
      tooltipEl.style.fontFamily = 'inherit';

      tooltipEl.innerHTML = tooltipContent;
    },
  };

  const customTooltipforDealsAdmin = {
    enabled: false,
    external: function (context: any) {
      const { tooltipEl, tooltipModel } = initiateUnderwritingChartsTooltip(context);

      const dealName = tooltipModel.title?.[0] ?? 'Unknown';

      // Filter and group by dealName for the current referrer
      const referrerDeals = plotData
        .filter((item: any) => item.dealName === dealName)
        .reduce((acc: Record<string, number>, curr: any) => {
          acc[curr.referrer] = (acc[curr.referrer] || 0) + Number(curr.commission);
          return acc;
        }, {});

      // Tooltip header
      let tooltipContent = `<div style="font-weight: bold; margin-bottom: 8px; font-size: 1rem;">${dealName}</div>`;

      // Table headers
      tooltipContent += `
          <div style="display: flex; justify-content: space-between; font-weight: 500; color: #6C6C6C; margin-bottom: 4px; font-size: 0.75rem;">
            <div>Referrer</div>
            <div>Commissions</div>
          </div>
        `;

      // Deal rows
      Object.entries(referrerDeals).forEach(([referrer, commission]) => {
        tooltipContent += `
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; color: #6C6C6C; font-size: 0.875rem; font-weight: 700;">
              <div>${referrer}</div>
              <div>${formatCurrency(commission.toFixed(2)) || '$0'}</div>
            </div>
          `;
      });

      // Tooltip styling
      tooltipEl.style.width = '250px';
      tooltipEl.style.height = 'auto';
      tooltipEl.style.padding = '12px';
      tooltipEl.style.background = '#fff';
      tooltipEl.style.borderRadius = '8px';
      tooltipEl.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
      tooltipEl.style.fontFamily = 'inherit';

      tooltipEl.innerHTML = tooltipContent;
    },
  };

  const customTooltipforDealsViewer = {
    enabled: false,
    external: function (context: any) {
      const { tooltipEl, tooltipModel } = initiateUnderwritingChartsTooltip(context);

      const dealName = tooltipModel.title?.[0] ?? 'Unknown';

      // Filter and group by dealName for the current referrer
      const referrerDeals = plotData
        .filter((item: any) => item.dealName === dealName)
        .reduce((acc: Record<string, number>, curr: any) => {
          acc[curr.referrer] = (acc[curr.referrer] || 0) + Number(curr.commission);
          return acc;
        }, {});

      // Table headers
      let tooltipContent = `
          <div style="display: flex; justify-content: space-between; font-weight: 500; color: #6C6C6C; margin-bottom: 4px; font-size: 0.75rem;">
            <div>Deal</div>
            <div>Commissions</div>
          </div>
        `;

      // Deal rows
      Object.entries(referrerDeals).forEach(([referrer, commission]) => {
        tooltipContent += `
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px; color: #162936; font-size: 0.875rem; font-weight: 700;">
              <div>${dealName}</div>
              <div>${formatCurrency(commission.toFixed(2)) || '$0'}</div>
            </div>
          `;
      });

      // Tooltip styling
      tooltipEl.style.width = '250px';
      tooltipEl.style.height = 'auto';
      tooltipEl.style.padding = '12px';
      tooltipEl.style.background = '#fff';
      tooltipEl.style.borderRadius = '8px';
      tooltipEl.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
      tooltipEl.style.fontFamily = 'inherit';

      tooltipEl.innerHTML = tooltipContent;
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    layout: {
      padding: {
        bottom: 50,
        left: 50,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: isReferrers
        ? customTooltipforReferrers
        : userRole === 'ADMIN'
          ? customTooltipforDealsAdmin
          : customTooltipforDealsViewer,
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (val: any) => formatCurrency(val.toFixed(2)),
          color: '#989CA2',
          font: {
            family: 'Manrope',
            weight: 700,
          },
        },
        grid: {
          display: true,
          color: '#AECBD0',
          tickWidth: 0,
        },
        border: {
          color: '#AECBD0',
          dash: [5, 5],
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#989CA2',
          font: {
            family: 'Manrope',
            weight: 700,
          },
        },
        border: {
          color: '#AECBD0',
          dash: [5, 5],
        },
      },
    },
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 'fit-content',
        backgroundColor: '#FFF',
        borderRadius: '0.75rem',
        padding: '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
      }}
    >
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 700,
          color: '#162936',
          lineHeight: '1.375rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Commissions by {isReferrers ? 'Referrer' : 'Deal'}
        <IconButton
          onClick={() => handleDownload(selectedOptions)}
          sx={{
            color: '#000',
            padding: '4px',
          }}
        >
          <FileDownloadOutlinedIcon />
        </IconButton>
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '1rem',
        }}
      >
        <FormControl
          sx={{
            width: '300px',
          }}
        >
          <Select
            labelId="payout-month-label"
            id="payout-month"
            multiple
            value={selectedOptions}
            displayEmpty
            onChange={(e) => handleChange('payout-month', e.target.value as string[])}
            renderValue={(selected) =>
              selected.length === 0
                ? 'Payout Month'
                : `Payout Month: ${payoutOptions.filter((option) => selected.includes(option)).join(', ')}`
            }
            MenuProps={{
              sx: { zIndex: 10000 },
              PaperProps: {
                sx: {
                  maxHeight: '20rem',
                  backgroundColor: '#F0F0F1',
                  '& .Mui-selected': {
                    backgroundColor: '#AECBD0',
                  },
                },
              },
            }}
            sx={{
              width: '100%',
              height: '2.5rem',
              borderRadius: '0.5rem',
              color: '#6C6C6C',
              fontWeight: selectedOptions.length ? 700 : 500,
              fontSize: '0.875rem',
            }}
          >
            {/* Select All */}
            <MenuItem value={ALL_VALUE}>
              <Checkbox
                checked={isAllSelected}
                icon={<IndeterminateCheckBoxOutlinedIcon />}
                checkedIcon={<IndeterminateCheckBoxOutlinedIcon />}
              />
              <ListItemText primary="Select All" />
            </MenuItem>

            {/* Individual Options */}
            {payoutOptions.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={selectedOptions.includes(option)} />
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box
          sx={{
            backgroundColor: '#2F736E14',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem',
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: '#6C6C6C',
              lineHeight: '1.25rem',
              whiteSpace: 'nowrap',
            }}
          >
            Final Commission Amount •{' '}
            {formatCurrency(
              plotData.reduce((sum, entry) => sum + Number(entry.commission), 0).toFixed(2),
            )}
          </Typography>
        </Box>
      </Box>
      <Box style={{ height: '400px', width: '95%' }}>
        <Bar
          data={data}
          options={options as any}
          plugins={[customXandYAxisLabelPlugin(xLabel, yLabel), hoverLinePlugin]}
        />
      </Box>
    </Box>
  );
};

export default CommissionsChart;
